import { ReactNode, useState, useEffect } from 'react';
import { LiaChatDrawer } from '@/components/LiaChatDrawer';
import { HelpCenter } from '@/components/help/HelpCenter';
// import { WhatsAppOptInModal } from '@/components/WhatsAppOptInModal'; // Desactivado hasta arrancar piloto WhatsApp
import { useAuth, AppRole } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { NotificationsDropdown } from '@/components/NotificationsDropdown';
import { AnnouncementBanner } from '@/components/public/AnnouncementBanner';
import { AnnouncementPopover } from '@/components/public/AnnouncementPopover';
import { useAnnouncementBanner } from '@/hooks/useAnnouncementBanner';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { useRecordActivity } from '@/hooks/useRecordActivity';
import { useOrgPlan } from '@/hooks/useOrgPlan';
import { UpgradePlanDialog } from '@/components/admin/UpgradePlanDialog';
import logoLiderIA from '@/assets/logo_lider_ia_white.png';
import { SidebarFooterLinks } from '@/components/SidebarFooterLinks';
import { SidebarRoleSwitcher } from '@/components/sidebar/SidebarRoleSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard, Target, Layers, Users, Settings, Flame, BookOpen,
  Calendar, MessageCircle, BarChart3, FolderKanban, ChevronRight, ChevronDown,
  Sparkles, BarChart2, ClipboardList, Stethoscope, TrendingUp, LogOut,
  RefreshCw, UserCircle, Shield, User, Wrench, Headphones, Building2,
  Video, Brain, FileStack, GraduationCap, SlidersHorizontal, Trophy, CreditCard,
  Menu, Tag, Sun, Moon, UserCog, X, Radio, Mail, Receipt, Presentation, FileSearch,
  HelpCircle, Briefcase, CalendarDays,
  Lock,
  FlaskConical,
} from 'lucide-react';
import { useUserAccess } from '@/hooks/useUserAccess';
import { usePublicRoutesEnabled } from '@/hooks/usePublicRoutesEnabled';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: NavItem[];
  /** Módulo visible pero no disponible con el plan gratuito empresarial. */
  locked?: boolean;
}

interface NavItemWithSeparator extends NavItem {
  separatorAfter?: boolean;
}

function getParticipantNav(hasPaidAccess: boolean, hasActiveCohort: boolean, routesEnabled: boolean): NavItemWithSeparator[] {
  const items: NavItemWithSeparator[] = [
    { label: 'Inicio', path: '/dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
    { label: 'Diagnóstico', path: '/diagnostic', icon: <Stethoscope className="w-5 h-5" /> },
    { label: 'Contenido', path: '/content', icon: <BookOpen className="w-5 h-5" /> },
    ...(hasActiveCohort ? [{ label: 'Calendario', path: '/calendar', icon: <Calendar className="w-5 h-5" /> }] : []),
    { label: 'Laboratorio de Reto y Solución', path: '/challenge-lab', icon: <FlaskConical className="w-5 h-5" /> },
    { label: 'Proyectos', path: '/challenge', icon: <Target className="w-5 h-5" /> },
    ...(routesEnabled ? [{ label: 'Rutas', path: '/rutas', icon: <GraduationCap className="w-5 h-5" />, separatorAfter: true }] : [{ label: 'Tendencias de IA', path: '/news', icon: <TrendingUp className="w-5 h-5" /> }]),
  ];

  if (routesEnabled) {
    items.push({ label: 'Tendencias de IA', path: '/news', icon: <TrendingUp className="w-5 h-5" /> });
  }

  items.push(
    { label: 'Herramientas de IA', path: '/ai-tools', icon: <Wrench className="w-5 h-5" /> },
    { label: 'Comunidad', path: '/forum', icon: <MessageCircle className="w-5 h-5" />, separatorAfter: true },
    { label: 'Planes', path: hasPaidAccess ? '/profile?tab=pagos' : '/planes', icon: <CreditCard className="w-5 h-5" /> }
  );

  if (hasPaidAccess) {
    items.push({ label: 'Soporte', path: '/support', icon: <Headphones className="w-5 h-5" /> });
  }

  items.push({ label: 'Empresas', path: '/empresas', icon: <Building2 className="w-5 h-5" /> });

  return items;
}
const mentorNav: NavItem[] = [
  { label: 'Inicio', path: '/dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
  { label: 'Estudiantes', path: '/students', icon: <Users className="w-5 h-5" /> },
  { label: 'Mentorías Personalizadas', path: '/mentorias', icon: <Briefcase className="w-5 h-5" /> },
  { label: 'Calendario', path: '/calendar', icon: <Calendar className="w-5 h-5" /> },
  { label: 'Calendario Unificado', path: '/calendario-unificado', icon: <CalendarDays className="w-5 h-5" /> },
  { label: 'Comunidad', path: '/forum', icon: <MessageCircle className="w-5 h-5" /> },
];

const adminNav: NavItemWithSeparator[] = [
  { label: 'Inicio', path: '/dashboard', icon: <BarChart2 className="w-5 h-5" /> },
  { label: 'Diagnóstico Empresarial', path: '/enterprise-diagnostic', icon: <ClipboardList className="w-5 h-5" /> },
  { label: 'Gestión de Usuarios', path: '/user-management', icon: <Users className="w-5 h-5" /> },
  { label: 'Seguimiento de Usuarios', path: '/user-tracking', icon: <TrendingUp className="w-5 h-5" /> },
  { label: 'Gestión de Cohortes', path: '/cohort-management', icon: <Calendar className="w-5 h-5" /> },
  { label: 'Portafolio de Proyectos', path: '/ai-portfolio', icon: <Layers className="w-5 h-5" /> },
  { label: 'Mentorías Personalizadas', path: '/mentorias', icon: <Briefcase className="w-5 h-5" /> },
  { label: 'Soporte', path: '/support', icon: <Headphones className="w-5 h-5" /> },
  { label: 'Dashboard Empresarial', path: '/enterprise-dashboard', icon: <Building2 className="w-5 h-5" />, separatorAfter: true },
  { label: 'Inventario IA', path: '/ai-inventory', icon: <Wrench className="w-5 h-5" /> },
  { label: 'Gestión de Riesgos', path: '/risk-management', icon: <Shield className="w-5 h-5" /> },
  { label: 'Centro de Excelencia IA', path: '/coe-management', icon: <Sparkles className="w-5 h-5" /> },
  { label: 'Gestión del Cambio', path: '/change-management', icon: <RefreshCw className="w-5 h-5" /> },
  { label: 'Auditoría IA', path: '/ai-conversations-audit', icon: <FileSearch className="w-5 h-5" /> },
  { label: 'Configuración', path: '/settings', icon: <Settings className="w-5 h-5" /> },
];

const superAdminNav: NavItemWithSeparator[] = [
  { label: 'Inicio', path: '/dashboard', icon: <BarChart2 className="w-5 h-5" /> },

  { label: 'Diagnóstico Empresarial', path: '/enterprise-diagnostic', icon: <ClipboardList className="w-5 h-5" /> },
  { label: 'Gestión de Usuarios', path: '/user-management', icon: <Users className="w-5 h-5" /> },
  { label: 'Seguimiento de Usuarios', path: '/user-tracking', icon: <TrendingUp className="w-5 h-5" /> },
  { label: 'Organizaciones', path: '/organizations', icon: <Building2 className="w-5 h-5" /> },
  { label: 'Gestión de Cohortes', path: '/cohort-management', icon: <Calendar className="w-5 h-5" /> },
  { label: 'Mentorías Personalizadas', path: '/mentorias', icon: <Briefcase className="w-5 h-5" /> },
  { label: 'Calendario Unificado', path: '/calendario-unificado', icon: <CalendarDays className="w-5 h-5" /> },
  { label: 'Portafolio de Proyectos', path: '/ai-portfolio', icon: <Layers className="w-5 h-5" /> },
  { label: 'Tickets de Soporte', path: '/tickets', icon: <Headphones className="w-5 h-5" /> },
  { label: 'Dashboard Empresarial', path: '/enterprise-dashboard', icon: <Building2 className="w-5 h-5" />, separatorAfter: true },
  { label: 'Inventario IA', path: '/ai-inventory', icon: <Wrench className="w-5 h-5" /> },
  { label: 'Gestión de Riesgos', path: '/risk-management', icon: <Shield className="w-5 h-5" /> },
  { label: 'Centro de Excelencia IA', path: '/coe-management', icon: <Sparkles className="w-5 h-5" /> },
  { label: 'Gestión del Cambio', path: '/change-management', icon: <RefreshCw className="w-5 h-5" /> },
  { label: 'Auditoría IA', path: '/ai-conversations-audit', icon: <FileSearch className="w-5 h-5" />, separatorAfter: true },
  {
    label: 'Admon Académica',
    path: '#',
    icon: <GraduationCap className="w-5 h-5" />,
    children: [
      { label: 'Gestión de Contenido', path: '/content-management', icon: <Video className="w-5 h-5" /> },
      { label: 'Gestión de Rutas', path: '/program-management', icon: <GraduationCap className="w-5 h-5" /> },
      { label: 'Gestión de Recursos', path: '/resource-management', icon: <FileStack className="w-5 h-5" /> },
    ],
  },
  {
    label: 'Operación de Rutas',
    path: '#',
    icon: <Wrench className="w-5 h-5" />,
    children: [
      { label: 'Evaluaciones IA', path: '/ai-evaluations', icon: <Brain className="w-5 h-5" /> },
      { label: 'Marcado masivo', path: '/bulk-completion', icon: <Wrench className="w-5 h-5" /> },
      { label: 'Sesiones en Vivo', path: '/live-sessions', icon: <Radio className="w-5 h-5" /> },
      { label: 'Comunicados', path: '/comunicados', icon: <Mail className="w-5 h-5" /> },
      { label: 'Logs de WhatsApp', path: '/admin/whatsapp-logs', icon: <MessageCircle className="w-5 h-5" /> },
    ],
  },
  {
    label: 'Admon Marketing',
    path: '#',
    icon: <TrendingUp className="w-5 h-5" />,
    children: [
      { label: 'Gestión de Tendencias IA', path: '/trends-management', icon: <TrendingUp className="w-5 h-5" /> },
      { label: 'Gestión de Impacto', path: '/impact-management', icon: <Sparkles className="w-5 h-5" /> },
      { label: 'Interesados', path: '/route-interests', icon: <Users className="w-5 h-5" /> },
      { label: 'Talleres', path: '/admin/talleres', icon: <Presentation className="w-5 h-5" /> },
      { label: 'Adquisición y Campañas', path: '/admin/adquisicion', icon: <TrendingUp className="w-5 h-5" /> },
    ],
  },
  {
    label: 'Admon Financiera',
    path: '#',
    icon: <CreditCard className="w-5 h-5" />,
    children: [
      { label: 'Gestión de Pagos', path: '/payment-management', icon: <CreditCard className="w-5 h-5" /> },
      { label: 'Códigos de Descuento', path: '/discount-codes', icon: <Tag className="w-5 h-5" /> },
      { label: 'Liquidaciones Expertos', path: '/payouts', icon: <Receipt className="w-5 h-5" /> },
    ],
  },
  { label: 'Config. Plataforma', path: '/platform-config', icon: <SlidersHorizontal className="w-5 h-5" /> },
  { label: 'Panel Ejecutivo', path: '/admin/executive', icon: <Sparkles className="w-5 h-5" /> },
];

// Auditor: rol de SOLO LECTURA scopeado por cohortes asignadas. Ve únicamente
// los 4 módulos de seguimiento. El allow-list de rutas está en App.tsx.
const auditorNav: NavItemWithSeparator[] = [
  { label: 'Gestión de Usuarios', path: '/user-management', icon: <Users className="w-5 h-5" /> },
  { label: 'Gestión de Cohortes', path: '/cohort-management', icon: <Calendar className="w-5 h-5" /> },
  { label: 'Portafolio de Proyectos', path: '/ai-portfolio', icon: <Layers className="w-5 h-5" /> },
  { label: 'Dashboard Empresarial', path: '/enterprise-dashboard', icon: <Building2 className="w-5 h-5" /> },
];

function getRoleTagline(role: AppRole | null): string | null {
  if (role === 'participant') return 'Aprende e Innova';
  if (role === 'admin') return 'Gestiona y Gobierna';
  if (role === 'auditor') return 'Seguimiento y Auditoría';
  return null;
}

const roleLabels: Record<AppRole, { label: string; icon: React.ReactNode }> = {
  participant: { label: 'Participante', icon: <UserCircle className="w-4 h-4" /> },
  mentor: { label: 'Mentor', icon: <Trophy className="w-4 h-4" /> },
  admin: { label: 'Administrador', icon: <Shield className="w-4 h-4" /> },
  super_admin: { label: 'Super Admin', icon: <Settings className="w-4 h-4" /> },
  auditor: { label: 'Auditor', icon: <FileSearch className="w-4 h-4" /> },
};

function NavItemComponent({ item, onLockedClick }: { item: NavItem; onLockedClick?: (label: string) => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  return (
    <li>
      <div className="flex flex-col">
        <NavLink
          to={item.path}
          onClick={(e) => {
            if (item.locked) { e.preventDefault(); onLockedClick?.(item.label); return; }
            if (hasChildren) { e.preventDefault(); setIsOpen(!isOpen); }
          }}
          className={({ isActive: linkActive }) =>
            cn(
              'flex items-center justify-between px-4 py-1 rounded-xl text-[15.5px] transition-all duration-300 group',
              item.locked
                ? 'text-sidebar-foreground/40 hover:bg-sidebar-accent/50'
                : linkActive && !hasChildren
                ? 'bg-primary text-primary-foreground font-semibold shadow-lg shadow-primary/25'
                : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground'
            )
          }
        >
          <div className="flex items-center gap-3">
            <span className="transition-transform duration-300 group-hover:scale-110">{item.icon}</span>
            <span>{item.label}</span>
          </div>
          {item.locked ? (
            <Lock className="w-3.5 h-3.5 text-sidebar-foreground/40" />
          ) : hasChildren ? (
            <span className="text-sidebar-foreground/60">
              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </span>
          ) : null}
        </NavLink>
        
        {hasChildren && isOpen && (
          <ul className="ml-6 mt-2 space-y-1 border-l-2 border-sidebar-border pl-4">
            {item.children?.map((child) => (
              <li key={child.path}>
                <NavLink
                  to={child.path}
                  className={({ isActive: childActive }) =>
                    cn(
                      'flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all duration-300',
                      childActive
                        ? 'bg-sidebar-primary/20 text-sidebar-primary font-medium'
                        : 'text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground'
                    )
                  }
                >
                  {child.icon}
                  <span>{child.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

export function AppLayout({ children }: { children: ReactNode }) {
  const { currentRole, setCurrentRole, profile, roles, signOut, user, isImpersonating, impersonatedAs, stopImpersonation } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { hasPaidAccess, hasActiveCohort } = useUserAccess();
  const { enabled: routesEnabled } = usePublicRoutesEnabled();
  const { data: announcement } = useAnnouncementBanner();
  const announcementActive = !!(announcement?.enabled && announcement.text);
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [liaChatOpen, setLiaChatOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const { recordActivity } = useRecordActivity();
  const { isFreePlan, canAccess } = useOrgPlan();
  const [lockedModule, setLockedModule] = useState<string | null>(null);

  // Record daily activity on any authenticated page visit
  useEffect(() => {
    recordActivity();
  }, [recordActivity]);

  const displayName = profile?.full_name || 'Usuario';
  const displayCompany = profile?.company || profile?.email || '';
  const initials = displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    await signOut();
    // Recarga limpia: evita que un estado residual (iOS Safari) redirija de vuelta al panel.
    window.location.replace("/auth");
  };

  const baseNavItems = currentRole === 'participant'
    ? getParticipantNav(hasPaidAccess, hasActiveCohort, routesEnabled)
    : currentRole === 'mentor'
    ? mentorNav
    : currentRole === 'super_admin'
    ? superAdminNav
    : currentRole === 'auditor'
    ? auditorNav
    : currentRole === 'admin'
    ? adminNav
    : getParticipantNav(hasPaidAccess, hasActiveCohort, routesEnabled);

  // Plan gratuito empresarial: los módulos de gobierno de IA se ven en el menú
  // pero bloqueados, con invitación a mejorar de plan.
  const navItems = isFreePlan && currentRole === 'admin'
    ? baseNavItems.map((item) => ({ ...item, locked: !canAccess(item.path) }))
    : baseNavItems;

  return (
    <div className="min-h-screen bg-background bg-pattern">
      <aside className="w-72 gradient-dark flex-col shadow-2xl overflow-hidden fixed top-0 left-0 h-screen z-50 hidden lg:flex">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-secondary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 -left-10 w-32 h-32 bg-accent/10 rounded-full blur-3xl" />
        </div>

        <div className="p-4 border-b border-sidebar-border relative z-10">
          <div className="flex flex-col items-start cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate("/")}>
            <img src={logoLiderIA} alt="LÍDER IA" className="h-8 w-auto" />
            {getRoleTagline(currentRole) && (
              <p className="mt-2 text-xs font-medium text-sidebar-foreground/70 tracking-wide">
                {getRoleTagline(currentRole)}
              </p>
            )}
          </div>
        </div>

        <nav className="flex-1 px-3 py-2 overflow-y-auto relative z-10 scrollbar-custom">
          <ul className="space-y-1.5">
            {navItems.map((item, index) => (
              <div key={item.path + index}>
                <NavItemComponent item={item} onLockedClick={setLockedModule} />
                {(item as NavItemWithSeparator).separatorAfter && (
                  <div className="my-2 mx-4 border-t border-white/20" />
                )}
              </div>
            ))}
          </ul>
        </nav>
        <SidebarRoleSwitcher />
        <SidebarFooterLinks showLegalLinks={false} showSocials={roles.length < 2} />

        <div className="p-4 relative z-10">

          <button onClick={() => setLiaChatOpen(true)} className="w-full flex items-center justify-center gap-3 px-4 py-4 bg-gradient-to-r from-secondary to-primary text-white rounded-2xl font-semibold text-sm hover:shadow-lg hover:shadow-secondary/40 transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0">
            <MessageCircle className="w-5 h-5" />
            <span>Chat con LIA</span>
            <Sparkles className="w-4 h-4 animate-pulse" />
          </button>
        </div>
      </aside>

      <div className="lg:ml-72 min-h-screen flex flex-col">
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur-xl flex items-center justify-between px-4 sm:px-6 sticky top-0 z-40 animate-fade-in">
          <div className="flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0 gradient-dark border-r-0 flex flex-col">
                <div className="p-4 border-b border-white/10">
                  <div className="flex flex-col items-start cursor-pointer hover:opacity-80 transition-opacity" onClick={() => { navigate("/"); setMobileOpen(false); }}>
                    <img src={logoLiderIA} alt="LÍDER IA" className="h-8 w-auto" />
                    {getRoleTagline(currentRole) && (
                      <p className="mt-2 text-xs font-medium text-sidebar-foreground/70 tracking-wide">
                        {getRoleTagline(currentRole)}
                      </p>
                    )}
                  </div>
                </div>
                <nav className="flex-1 px-3 py-2 overflow-y-auto scrollbar-custom">
                  <ul className="space-y-1.5">
                    {navItems.map((item, index) => (
                      <div key={item.path + index}>
                        <li>
                          <NavLink
                            to={item.path}
                            onClick={(e) => {
                              if ((item as NavItem).locked) {
                                e.preventDefault();
                                setMobileOpen(false);
                                setLockedModule(item.label);
                                return;
                              }
                              setMobileOpen(false);
                            }}
                            className={({ isActive }) =>
                              cn(
                                'flex items-center gap-3 px-4 py-2 rounded-xl text-[15.5px] transition-all duration-300',
                                (item as NavItem).locked
                                  ? 'text-sidebar-foreground/40'
                                  : isActive
                                  ? 'bg-primary text-primary-foreground font-semibold shadow-lg shadow-primary/25'
                                  : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground'
                              )
                            }
                          >
                            {item.icon}
                            <span className="flex-1">{item.label}</span>
                            {(item as NavItem).locked && <Lock className="w-3.5 h-3.5 opacity-60" />}
                          </NavLink>
                        </li>
                        {(item as NavItemWithSeparator).separatorAfter && (
                          <div className="my-2 mx-4 border-t border-white/20" />
                        )}
                      </div>
                    ))}
                  </ul>
                </nav>
                <SidebarRoleSwitcher />
                <SidebarFooterLinks showLegalLinks={false} showSocials={roles.length < 2} />
                <div className="p-4">

                  <button onClick={() => { setMobileOpen(false); setLiaChatOpen(true); }} className="w-full flex items-center justify-center gap-3 px-4 py-4 bg-gradient-to-r from-secondary to-primary text-white rounded-2xl font-semibold text-sm hover:shadow-lg hover:shadow-secondary/40 transition-all duration-300">
                    <MessageCircle className="w-5 h-5" />
                    <span>Chat con LIA</span>
                    <Sparkles className="w-4 h-4 animate-pulse" />
                  </button>
                  <button onClick={() => { setMobileOpen(false); handleLogout(); }} className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white/80 border border-white/20 hover:bg-white/10 transition-colors">
                    <LogOut className="w-4 h-4" />
                    <span>Cerrar sesión</span>
                  </button>
                </div>
              </SheetContent>
            </Sheet>
            {announcementActive ? (
              <>
                <div className="hidden sm:block min-w-0 flex-1"><AnnouncementBanner /></div>
              </>
            ) : (
              <h2 className="hidden sm:block text-lg font-semibold text-foreground">
                {currentRole === 'participant' ? 'Aprende e Innova'
                  : currentRole === 'mentor' ? 'Portal del Mentor'
                  : currentRole === 'super_admin' ? 'Portal Super Admin'
                  : 'Gestiona y Gobierna'}
              </h2>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="sm:hidden"><AnnouncementPopover /></div>
            <button
              onClick={() => setHelpOpen(true)}
              aria-label="Centro de ayuda"
              className="w-10 h-10 rounded-xl flex items-center justify-center text-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
            <NotificationsDropdown />
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 px-3 py-2 rounded-xl bg-muted/50 hover:bg-muted transition-colors cursor-pointer">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary via-primary to-accent flex items-center justify-center text-white font-bold text-sm shadow-lg ring-2 ring-white/10">
                    {initials}
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-semibold text-foreground">{displayName}</p>
                    <p className="text-xs text-muted-foreground">{displayCompany}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-background border border-border shadow-lg z-50">
                <DropdownMenuLabel className="flex flex-col gap-0.5">
                  <span>Mi cuenta</span>
                  <span className="text-xs font-normal text-muted-foreground truncate">{user?.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuItem asChild className="gap-2">
                  <Link to="/profile">
                    <User className="w-4 h-4" />
                    Mi Perfil
                  </Link>
                </DropdownMenuItem>
                
                {roles.length > 1 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">Cambiar rol</DropdownMenuLabel>
                    {roles.map((role) => (
                      <DropdownMenuItem key={role} onClick={() => setCurrentRole(role)} className="gap-2">
                        {roleLabels[role].icon}
                        {roleLabels[role].label}
                        {currentRole === role && <span className="ml-auto text-primary">✓</span>}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}

                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={(e) => { e.preventDefault(); toggleTheme(); }} className="gap-2">
                  {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  {theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="gap-2 text-destructive focus:text-destructive">
                  <LogOut className="w-4 h-4" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {isImpersonating && impersonatedAs && (
          <div className="bg-amber-500 text-white px-4 py-2.5 flex items-center justify-between gap-4 text-sm font-medium z-30 sticky top-16">
            <div className="flex items-center gap-2 min-w-0">
              <UserCog className="w-4 h-4 shrink-0" />
              <span className="truncate">
                Vista de administrador como: <strong>{impersonatedAs.name}</strong>
                <span className="hidden sm:inline font-normal opacity-80"> · {impersonatedAs.email}</span>
              </span>
            </div>
            <button
              onClick={async () => { await stopImpersonation(); navigate('/user-management'); }}
              className="flex items-center gap-1.5 shrink-0 bg-white/20 hover:bg-white/30 transition-colors px-3 py-1 rounded-full text-xs font-semibold"
            >
              <X className="w-3.5 h-3.5" />
              Salir de vista
            </button>
          </div>
        )}

        <main className="flex-1 p-4 sm:p-6 overflow-auto">
          <div className="animate-fade-in max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>

      <UpgradePlanDialog
        open={!!lockedModule}
        onOpenChange={(open) => !open && setLockedModule(null)}
        moduleName={lockedModule ?? undefined}
      />

      <LiaChatDrawer isOpen={liaChatOpen} onClose={() => setLiaChatOpen(false)} />
      <HelpCenter open={helpOpen} onOpenChange={setHelpOpen} />
      {/* <WhatsAppOptInModal /> — desactivado hasta arrancar piloto WhatsApp */}
    </div>
  );
}
